import React, { useEffect, useRef } from 'react';
import './HeroRepulsion.css';

/**
 * Hero headline, repulsion treatment.
 *
 * Desktop only, hover only, decorative. "Wear the mask. Own the silence."
 * sits as it always has -- real text, real entrance animation, fully
 * accessible, zero JS dependency to be read. This component adds nothing
 * to that guarantee; it can only ever draw OVER an already-correct page,
 * never replace the reason the page is correct.
 *
 * On a capable pointer, hovering the headline fades the real text out and
 * a canvas-drawn cloud of thread-points fades in at the exact same glyph
 * positions. The points nearest the cursor scatter and spring back as it
 * moves, like disturbing loose fibre. Move away and it settles back to the
 * plain static headline, canvas cleared, loop stopped.
 *
 * THE SAFETY RULE THIS COMPONENT EXISTS TO FOLLOW, learned from a real bug:
 * sampling the real glyphs into points can come back EMPTY for reasons that
 * have nothing to do with anything being broken (a font not loaded yet, a
 * sub-pixel measurement, a browser quirk). If that happens, the crossfade
 * must never run -- the real h1 stays at its default opacity forever, and
 * this component simply does nothing. Hiding real text in exchange for a
 * canvas effect is only ever allowed once the effect has proven it has
 * something to draw.
 */

const MIN_POINTS = 60; // below this, something is wrong with the sample -- never engage.

// The canvas used to be sized exactly to the headline's own tight box, so a
// point scattered upward off "Wear the mask." -- the top line, with nothing
// above it inside that box -- had nowhere to draw and simply vanished at the
// canvas edge: not hidden, outside the canvas's own pixel area entirely,
// the same way nothing can be painted past the edge of a canvas. PAD gives
// the canvas real room on every side so a scattered point still has
// somewhere to be drawn. MAX_DISPLACEMENT then keeps the physics itself
// from ever asking for more room than that, however fast or long the
// cursor sweeps across it.
const PAD = 130;
const MAX_DISPLACEMENT = PAD * 0.8;

function capable() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

export default function HeroRepulsion({ active, wrapRef }) {
  const canvasRef = useRef(null);
  const engagedRef = useRef(false); // becomes true only after a real sample succeeded

  useEffect(() => {
    if (!active) return undefined;
    if (!capable()) return undefined;

    const wrap = wrapRef && wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return undefined;

    const title = wrap.querySelector('.home-hero-title');
    const lineSpans = wrap.querySelectorAll('.home-hero-line > span');
    if (!title || lineSpans.length === 0) return undefined;

    let cancelled = false;
    let raf = null;
    let points = [];
    let active_ = false; // pointer currently inside the title's area
    let mx = -9999, my = -9999;
    let settled = true; // true once every point is back at (or near) home and idle

    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Build the point cloud from the REAL rendered lines, not an assumed
    // layout. Each line is sampled where it actually sits, in its own font
    // and alignment, so this can never drift out of sync with a CSS change
    // to the headline made somewhere else.
    function sample() {
      const wrapRect = wrap.getBoundingClientRect();
      const W = wrapRect.width, H = wrapRect.height;
      if (!W || !H) return [];

      // The canvas is PAD larger than the headline's own box on every side,
      // and positioned to extend that far past the wrap (negative inset),
      // so a point scattered toward any edge -- including straight up off
      // the top line, which has nothing above it in the tight box -- still
      // has somewhere inside the canvas to be drawn.
      const padW = W + PAD * 2, padH = H + PAD * 2;
      canvas.width = Math.max(1, Math.round(padW * dpr));
      canvas.height = Math.max(1, Math.round(padH * dpr));
      canvas.style.width = padW + 'px';
      canvas.style.height = padH + 'px';
      canvas.style.left = -PAD + 'px';
      canvas.style.top = -PAD + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const pts = [];
      lineSpans.forEach((span) => {
        const text = span.textContent || '';
        if (!text.trim()) return;
        const r = span.getBoundingClientRect();
        const cs = window.getComputedStyle(span);
        // Shifted by PAD so a line's glyphs land in the padded canvas at the
        // same position they'd have in the old tight-fit one, with room to
        // scatter in every direction around them now.
        const localLeft = r.left - wrapRect.left + PAD;
        const localTop = r.top - wrapRect.top + PAD;
        const w = Math.max(1, Math.ceil(r.width));
        const h = Math.max(1, Math.ceil(r.height));

        const off = document.createElement('canvas');
        off.width = Math.max(1, Math.round(w * dpr));
        off.height = Math.max(1, Math.round(h * dpr));
        const o = off.getContext('2d');
        o.setTransform(dpr, 0, 0, dpr, 0, 0);
        o.fillStyle = '#fff';
        o.textBaseline = 'alphabetic';
        o.font = cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
        // Canvas letter-spacing is a newer API; where it's unsupported the
        // text renders a touch tighter, which only shifts where glyph
        // pixels land -- the sample is still valid words, not a failure.
        try { o.letterSpacing = cs.letterSpacing; } catch (e) { /* unsupported, fine */ }
        // Baseline placed from the real line-height so the sampled glyphs
        // sit where the real characters visually do, not at the box edge.
        const baselineY = h * 0.80;
        o.fillText(text, 0, baselineY);

        const img = o.getImageData(0, 0, off.width, off.height).data;
        // Integer step only -- a fractional step here produces a fractional
        // pixel index, and a typed array returns undefined (never a real
        // pixel value) for that, so the sample comes back silently empty.
        // That exact mistake shipped once; it is written out here for good.
        const step = 3;
        for (let y = 0; y < h; y += step) {
          for (let x = 0; x < w; x += step) {
            // dpr itself can be fractional (1.25/1.5 on Windows display
            // scaling), which makes y*dpr / x*dpr fractional even though
            // y/x are integers -- the same undefined-index failure as the
            // step comment above, just from the other factor. Floored so
            // the index is always a real integer pixel offset.
            const idx = (Math.floor(y * dpr) * off.width + Math.floor(x * dpr)) * 4 + 3;
            if (img[idx] > 128) {
              pts.push({ hx: localLeft + x, hy: localTop + y, x: localLeft + x, y: localTop + y, vx: 0, vy: 0 });
            }
          }
        }
      });
      return pts;
    }

    function frame() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgb(232,235,214)';

      // RADIUS was 64px against a headline that runs up to 92px tall per
      // line -- smaller than a single letter on a wide screen, so only a
      // tiny circle right under the cursor ever reacted and the rest of the
      // line sat still. Widened so one cursor position disturbs a real
      // stretch of the word, closer to the whole line feeling alive rather
      // than a small dot wobbling in place.
      const RADIUS = 150, FORCE = 480, SPRING = 0.07, DAMP = 0.82;
      let maxDisp = 0;

      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        p.vx += (p.hx - p.x) * SPRING;
        p.vy += (p.hy - p.y) * SPRING;

        if (active_) {
          const dx = p.x - mx, dy = p.y - my;
          const d2 = dx * dx + dy * dy;
          if (d2 < RADIUS * RADIUS) {
            const d = Math.sqrt(d2) || 0.001;
            const push = (1 - d / RADIUS) * FORCE;
            p.vx += (dx / d) * push * 0.016;
            p.vy += (dy / d) * push * 0.016;
          }
        }

        p.vx *= DAMP; p.vy *= DAMP;
        p.x += p.vx * 0.256;
        p.y += p.vy * 0.256;

        let dispX = p.x - p.hx, dispY = p.y - p.hy;
        let disp = Math.sqrt(dispX * dispX + dispY * dispY);
        // Clamped to what the padded canvas can actually hold, regardless
        // of how hard or long the cursor pushes. Pulling the point back to
        // the clamp also kills its outward velocity component, so it does
        // not keep straining against the limit every frame.
        if (disp > MAX_DISPLACEMENT) {
          const scale = MAX_DISPLACEMENT / disp;
          p.x = p.hx + dispX * scale;
          p.y = p.hy + dispY * scale;
          p.vx *= 0.3; p.vy *= 0.3;
          dispX = p.x - p.hx; dispY = p.y - p.hy;
          disp = MAX_DISPLACEMENT;
        }
        if (disp > maxDisp) maxDisp = disp;

        // Scaled against MAX_DISPLACEMENT (the real ceiling a point can
        // reach now) rather than the old fixed 26px, so dots keep growing
        // and fading across the whole widened range instead of maxing out
        // almost immediately.
        const dispNorm = Math.min(1, disp / MAX_DISPLACEMENT);
        ctx.globalAlpha = 0.55 + (1 - dispNorm) * 0.4;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.1 + dispNorm * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Idle-stop. Once the pointer has left and every point is back home,
      // stop scheduling frames rather than running an animation loop on
      // every visit to the homepage forever.
      settled = !active_ && maxDisp < 0.3;
      if (!settled) raf = requestAnimationFrame(frame);
      else raf = null;
    }

    function kick() { if (!raf && !cancelled) raf = requestAnimationFrame(frame); }

    function onEnter() {
      if (!engagedRef.current) return;
      wrap.classList.add('hero-repel-active');
      active_ = true;
      kick();
    }
    function onMove(e) {
      if (!engagedRef.current) return;
      const r = wrap.getBoundingClientRect();
      // Points live in canvas-local coordinates, shifted by +PAD from the
      // wrap (see sample() above). The cursor has to be shifted the same
      // way, or every distance check below is off by PAD in both axes --
      // the repulsion field ends up centered PAD px up-left of the real
      // cursor position.
      mx = e.clientX - r.left + PAD;
      my = e.clientY - r.top + PAD;
    }
    function onLeave() {
      active_ = false;
      mx = -9999; my = -9999;
      wrap.classList.remove('hero-repel-active');
      kick();
    }

    function engage() {
      const sampled = sample();
      if (cancelled) return;
      // The rule: fewer than MIN_POINTS is treated as a failed sample, not a
      // thin headline. Do not touch the real text. Do not attach listeners.
      if (sampled.length < MIN_POINTS) return;
      points = sampled;
      engagedRef.current = true;
      wrap.addEventListener('pointerenter', onEnter);
      wrap.addEventListener('pointermove', onMove);
      wrap.addEventListener('pointerleave', onLeave);
    }

    // Wait for the entrance to finish its transform (hero-rise, 820ms +
    // 70ms delay on the second line, defined in Home.css) before measuring
    // anything. Sampling mid-animation would capture the lines' transient,
    // still-moving position rather than where they actually come to rest,
    // and the point cloud would be built in the wrong place.
    //
    // A real 'animationend' is listened for rather than trusted to a fixed
    // delay alone, matching how the hero's own entrance already treats
    // timers as a fallback rather than the primary path. The timeout exists
    // only so a missed or renamed animation event can never leave this
    // waiting forever -- it does not need to be exact, because all it is
    // delaying is a decorative extra, not anything a visitor depends on to
    // see the page.
    const lastSpan = lineSpans[lineSpans.length - 1];
    let entranceSettled = false;
    const onEntranceEnd = () => {
      if (entranceSettled) return;
      entranceSettled = true;
      lastSpan.removeEventListener('animationend', onEntranceEnd);
      afterEntrance();
    };
    lastSpan.addEventListener('animationend', onEntranceEnd);
    const entranceTimeout = setTimeout(onEntranceEnd, 1100);

    function afterEntrance() {
      clearTimeout(entranceTimeout);
      if (cancelled) return;
      // Wait for the real font to be the one actually measured, so the
      // sampled glyphs match what a visitor is looking at, not a fallback.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(engage);
      } else {
        engage();
      }
    }

    function onResize() {
      if (!engagedRef.current) return;
      const sampled = sample();
      if (sampled.length < MIN_POINTS) {
        // A resize that breaks the sample disengages cleanly rather than
        // drawing a half-built effect.
        engagedRef.current = false;
        wrap.classList.remove('hero-repel-active');
        wrap.removeEventListener('pointerenter', onEnter);
        wrap.removeEventListener('pointermove', onMove);
        wrap.removeEventListener('pointerleave', onLeave);
        points = [];
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }
      points = sampled;
    }
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(entranceTimeout);
      lastSpan.removeEventListener('animationend', onEntranceEnd);
      window.removeEventListener('resize', onResize);
      wrap.removeEventListener('pointerenter', onEnter);
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerleave', onLeave);
      wrap.classList.remove('hero-repel-active');
    };
  }, [active, wrapRef]);

  return <canvas ref={canvasRef} className="hero-repel-canvas" aria-hidden="true" />;
}
